import api from '../store/axiosConfig'; // Use the configured axios instance
import { API_URL } from '../store/axiosConfig';

interface AuthResponse {
  user: {
    id: string;
    username: string;
    email: string;
    isPremium: boolean;
    isEmailVerified?: boolean;
  };
  token: string;
}

interface PasswordResetResponse {
  message: string;
  isGoogleAccount?: boolean;
}

export const authService = {
  async login(credentials: { email: string; password: string }): Promise<AuthResponse> {
    const response = await api.post('/auth/login', credentials);
    if (response.data.token) {
      localStorage.setItem('token', response.data.token);
    }
    return response.data;
  },

  async register(username: string, email: string, password: string): Promise<AuthResponse> {
    try {
      const response = await api.post('/auth/register', { username, email, password });
      if (response.data.token) {
        localStorage.setItem('token', response.data.token);
      }
      return response.data;
    } catch (error: unknown) {
      if (error && typeof error === 'object' && 'response' in error) {
        throw new Error((error as any).response?.data?.message || 'Registration failed');
      }
      throw new Error(error instanceof Error ? error.message : 'Registration failed');
    }
  },

  async googleLogin(credential: string): Promise<AuthResponse> {
    const response = await api.post('/auth/google', { token: credential });
    if (response.data.token) {
      localStorage.setItem('token', response.data.token);
    }
    return response.data;
  },

  async logout(): Promise<void> {
    localStorage.removeItem('token');
  },

  async sendVerificationEmail(): Promise<{ message: string }> {
    const response = await api.post('/auth/send-verification');
    return response.data;
  },

  async resendVerificationEmailPublic(email: string): Promise<{ message: string }> {
    const response = await api.post('/auth/resend-verification', { email });
    return response.data;
  },

  async initiateRegistration(username: string, email: string, password: string): Promise<any> {
    const response = await api.post('/auth/initiate-registration', {
      username, email, password
    });

    localStorage.setItem('pendingRegistration', JSON.stringify({
      username, email, password, timestamp: new Date().getTime()
    }));

    return response.data;
  },

  async verifyEmail(email: string, code: string): Promise<AuthResponse> {
    const pendingRegistrationStr = localStorage.getItem('pendingRegistration');
    let registrationData = null;

    if (pendingRegistrationStr) {
      const parsed = JSON.parse(pendingRegistrationStr);
      if (parsed.email === email) registrationData = parsed;
    }

    const response = await api.post('/auth/verify-email', {
      email, code, registrationData
    });

    if (response.data.token) {
      localStorage.setItem('token', response.data.token);
      localStorage.removeItem('pendingRegistration');
    }

    return response.data;
  },

  async requestPasswordReset(email: string): Promise<PasswordResetResponse> {
    const response = await api.post('/auth/request-password-reset', { email });
    return response.data;
  },

  async resetPassword(email: string, code: string, newPassword: string): Promise<PasswordResetResponse> {
    const response = await api.post('/auth/reset-password', {
      email, code, newPassword
    });
    return response.data;
  }
};
